import { useEffect, useState } from 'react'
import _ from 'lodash'
import {
  Button,
  Separator,
  Input,
  Grid,
  useToastAlert,
} from '@design-systems/canvas-ui'
import { renderRFI3QuestionWindow } from '../../common/rfiHelper'
import { SectionWrapper } from '../Builders'
import ProductHierarchySelector from '../RfiCommons/ProductHierarchySelector'
import FactoryHierarchyDetails from './FactoryHierarchyDetails'

const SEGMENT_FACTORY_CAPABILITY = 'factory_capability'
const CLIENT_FACTORY_CAPABILITIES = 'factory_capabilities'
const MESSAGE_CAPABILITIES_ADDED = 'More factory capabilities added below'

const FactoryCapabilities = (props) => {
  const makeToast = useToastAlert()
  const [sectionData, setSectionData] = useState(null)
  const [isTriggerSave, setIsTriggerSave] = useState(false)

  //Trigger save & refresh -> 1. TPC row deleted 2. Merch in any row selected
  const [isSaveAndRefresh, setIsSaveAndRefresh] = useState(false)

  //#region UseEffects

  useEffect(() => {
    setSectionData(props.sectionData)
  }, [props.sectionData])

  useEffect(() => {
    if (isTriggerSave && !_.isEqual(props.sectionData, sectionData)) {
      props.saveRfiPayload(sectionData, props.sectionTemplate.uid)
    }
  }, [isTriggerSave]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSaveAndRefresh) {
      props.saveRfiPayload(sectionData, props.sectionTemplate.uid, true)
      setIsSaveAndRefresh(false)
    }
  }, [isSaveAndRefresh]) // eslint-disable-line react-hooks/exhaustive-deps

  //#endregion

  /**
   * Update FC detail
   * @param {*} tpcIndex
   * @param {*} subSectionReferenceKey
   * @param {*} newAllTPCDetails
   */
  const updateFCDetail = (
    tpcIndex,
    subSectionReferenceKey,
    newAllTPCDetails,
  ) => {
    if (newAllTPCDetails) {
      const newSectionData = sectionData[subSectionReferenceKey].map(
        (eachTPC, index) => {
          return index !== tpcIndex
            ? eachTPC
            : { ...eachTPC, factory_capability: newAllTPCDetails }
        },
      )

      setSectionData({
        ...sectionData,
        [subSectionReferenceKey]: newSectionData,
      })
    }
  }

  /**
   * Render factory capability body
   * @returns
   */
  const renderFactoryCapabilitiesBody = () => {
    let fcReferenceKey = props.sectionTemplate.data.uid
    let allFcs = []

    if (sectionData) {
      let fcList = sectionData[fcReferenceKey]
      let sectionTemplatePath = `${props.sectionPath}.${fcReferenceKey}`

      allFcs = fcList?.map((eachFc, fcIndex) => {
        let sectionTPathIndex = `${sectionTemplatePath}[${fcIndex}]`
        return (
          <div key={`div-fc-${fcIndex}`} id={`div-fc-${fcIndex}`}>
            {/* 1. Header - Capability # */}
            <Grid.Container
              className="row-centre"
              key={`gc-fc-${fcIndex}`}
              id={`gc-fc-${fcIndex}`}
            >
              <Grid.Item size={{ xs: 10 }}>
                <Input.Label
                  className="hc8-fs-150 font-weight-500"
                  key={`lbl-fc-pc-count-${fcIndex}`}
                  id={`lbl-fc-pc-count-${fcIndex}`}
                >
                  {`Factory Capabilities ${fcIndex + 1}`}
                </Input.Label>
              </Grid.Item>
              {!props.isReadonly && (
                <Grid.Item size={{ xs: 2 }} className="align-self-right">
                  {props.sectionTemplate.remove && fcIndex > 0 && (
                    <Button
                      className="section-button"
                      key={`btn-delete-fc-${fcIndex}`}
                      data-testid={`btn-delete-fc-${fcIndex}`}
                      onClick={() => deleteFCUnit(fcIndex)}
                      variant="secondary"
                    >
                      {props.sectionTemplate.remove.label}
                    </Button>
                  )}
                </Grid.Item>
              )}
            </Grid.Container>

            {/* 2. Hierarchy Selector */}
            <ProductHierarchySelector
              clientType={CLIENT_FACTORY_CAPABILITIES}
              sectionTemplatePath={sectionTPathIndex}
              errorCache={props.errorCache}
              capabilitiesData={eachFc[SEGMENT_FACTORY_CAPABILITY]}
              rfiMasterData={props.rfiMasterData}
              tPCIndex={fcIndex}
              subSectionReferenceKey={fcReferenceKey}
              updateTPCHierarchy={updateFCHierarchyCapabilities}
              setIsSaveAndRefresh={setIsSaveAndRefresh}
              rfiType={props.rfiType}
              isReadonly={props.isReadonly}
            />

            <FactoryHierarchyDetails
              hierarchyDetail={eachFc[SEGMENT_FACTORY_CAPABILITY]}
              rfiMasterData={props.rfiMasterData}
              sectionTemplatePath={sectionTPathIndex}
              errorCache={props.errorCache}
              FCIndex={fcIndex}
              subSectionReferenceKey={fcReferenceKey}
              group={eachFc[SEGMENT_FACTORY_CAPABILITY]?.group_names} //Array of HH/AA/EB/FB
              updateFCDetail={updateFCDetail}
              isReadonly={props.isReadonly}
            />

            {fcIndex !== fcList?.length - 1 && (
              <Separator className="hc8-mb-2x" />
            )}
          </div>
        )
      })

      return props.isRfi3
        ? renderRFI3QuestionWindow(
            props.isRfi3Readonly,
            allFcs,
            sectionData?.rfi3_answers,
            props.sectionTemplate,
            props.rfiMasterData,
            (sectionRfi3Answers) =>
              setSectionData({
                ...sectionData,
                rfi3_answers: sectionRfi3Answers,
              }),
            props.supplierId,
            props.locationId,
            props.manufacturingTypeId,
            sectionData?.rfi3_files,
            (sectionRfiFiles) => {
              setIsTriggerSave(true)
              setSectionData({
                ...sectionData,
                rfi3_files: sectionRfiFiles,
              })
            },
            props.errorCache,
          )
        : allFcs
    }
  }

  /**
   * Delete FC row unit
   * @param {*} fCIndex
   * @param {*} subSectionListData
   */
  const deleteFCUnit = async (fCIndex) => {
    let fcReferenceKey = props.sectionTemplate.data.uid
    let allNewFcs = [...sectionData[fcReferenceKey]]
    allNewFcs.splice(fCIndex, 1)
    setSectionData({
      ...sectionData,
      [fcReferenceKey]: allNewFcs,
    })
    setIsSaveAndRefresh(true)
  }

  /**
   * Add FC unit
   */
  const addFCUnit = () => {
    let fcReferenceKey = props.sectionTemplate.data.uid
    let newFcItem = {
      factory_capability: {
        product_type: null,
        product_sub_type: null,
        merchandise_type: null,
        item_type: [],
        daily_factory_output: '',
        automation_level: null,
        capacity_percentage_open_for_target: '',
        capacity_percentage_penetrated_by_other_customers: '',
        manufacturing_packaging_season: [],
      },
    }
    makeToast({
      variant: 'success',
      heading: MESSAGE_CAPABILITIES_ADDED,
      message: '',
      autoHideDuration: 2000,
    })
    setSectionData({
      ...sectionData,
      [fcReferenceKey]: [...sectionData[fcReferenceKey], newFcItem],
    })
  }

  /**
   * Update FC Hierarchy capabilities (prod type, subtype, merchtype, itemtype)
   * @param {*} fcIndex
   * @param {*} subSectionReferenceKey
   * @param {*} newFCHierarchy
   */
  const updateFCHierarchyCapabilities = (
    fcIndex,
    subSectionReferenceKey,
    newFCHierarchy,
  ) => {
    if (newFCHierarchy) {
      const newSectionData = sectionData[subSectionReferenceKey].map(
        (eachFC, index) => {
          if (
            !newFCHierarchy.product_type ||
            !newFCHierarchy.product_sub_type ||
            !newFCHierarchy.merchandise_type
          ) {
            return index !== fcIndex
              ? eachFC
              : {
                  factory_capability: {
                    ...newFCHierarchy,
                    daily_factory_output: '',
                    automation_level: null,
                    capacity_percentage_open_for_target: '',
                    capacity_percentage_penetrated_by_other_customers: '',
                    manufacturing_packaging_season: [],
                  },
                }
          }

          return index !== fcIndex
            ? eachFC
            : { ...eachFC, factory_capability: newFCHierarchy }
        },
      )

      setSectionData({
        ...sectionData,
        [subSectionReferenceKey]: newSectionData,
      })
    }
  }

  return (
    <SectionWrapper
      sectionTemplate={props.sectionTemplate}
      sectionBody={renderFactoryCapabilitiesBody()}
      setIsTriggerSave={
        !props.isReadonly || props.isRfi3 ? setIsTriggerSave : () => {}
      }
      sectionPath={props.sectionPath}
      errorCache={props.errorCache}
      isRfi3={props.isRfi3}
      addMoreMetadata={
        !props.isReadonly
          ? {
              isAddMore: props.sectionTemplate.add_more,
              addMoreButtonText: props.sectionTemplate.add_more?.label,
              addMoreSubsectionReferenceKey: props.sectionTemplate.data.uid,
              addMore: () => addFCUnit(),
            }
          : ''
      }
    />
  )
}
export default FactoryCapabilities
